import {
    AssetType,
    BackgroundStyleType,
    ContentBlockType,
    HorizontalAlignType,
    PositionType,
    VerticalAlignType,
    TextBreakType
} from "common/constants";
import { CollectionElement, CollectionItemElement } from "../base/CollectionElement";
import { AdjustableContentElement, ContentElement } from "../base/ContentElement";
import { SVGCircleElement, SVGPolylineElement } from "../base/SVGElement";
import { BulletPoints } from "./TextGrid";
import { _ } from "js/vendor";
import { app } from "js/namespaces.js";
import * as geom from "js/core/utilities/geom";
import { BaseElement } from "js/elements/base/BaseElement";
import { TextElement } from "js/elements/base/TextElement";
import { BulletPointItem, NumberedListItem, TextListItem } from "js/elements/elements/TextGrid";
import { NodeElement } from "js/elements/base/NodeElement";
import { getValueOrDefault } from "../../core/utilities/extensions";
import { TextGroup } from "js/elements/base/TextGroup";

export class BaseContentBlockContainer extends BaseElement {
}

export class ContentBlockContainer extends BaseContentBlockContainer {
    get _canSelect() {
        return false;
    }

    get passThroughSelection() {
        return false;
    }

    get canSelectContentBlockFrame() {
        return true;
    }

    get isOverImage() {
        return super.getBackgroundColor(this) == BackgroundStyleType.IMAGE;
    }

    get availableBlockTypes() {
        if (this.options.allowedBlockTypes) {
            return this.options.allowedBlockTypes;
        }

        return [ContentBlockType.HEADLINE, ContentBlockType.HEADING, ContentBlockType.TITLE, ContentBlockType.BODY, ContentBlockType.CAPTION, ContentBlockType.BIGTEXT, ContentBlockType.LIST, ContentBlockType.DIVIDER, ContentBlockType.ICON];
    }

    get minItemCount() {
        return 0;
    }

    get defaultBlockType() {
        return getValueOrDefault(this.options.defaultBlockType, ContentBlockType.HEADLINE);
    }

    get canDelete() {
        return true;
    }

    get canDragPosition() {
        if (this.isOverImage) {
            return getValueOrDefault(this.options.canDragPosition, false);
        } else {
            return false;
        }
    }

    get canDragResize() {
        if (this.isOverImage) {
            return getValueOrDefault(this.options.canDragResize, false);
        } else {
            return false;
        }
    }

    get hasUserTransform() {
        return this.canDragPosition && (this.userPositionX != null && this.userPositionY != null);
    }

    get userWidth() {
        return this.model.userWidth;
    }

    get userPositionX() {
        return this.model.userPositionX;
    }

    get userPositionY() {
        return this.model.userPositionY;
    }

    get textPosition() {
        if (this.hasUserTransform) {
            return PositionType.USER;
        } else {
            return getValueOrDefault(this.model.textPosition, PositionType.CENTER);
        }
    }

    refreshElement(transition) {
        this.canvas.refreshElement(this, transition);
    }

    get canRefreshElement() {
        return true;
    }

    getDefaultTextAlign() {
        // this is for legacy contentblocks that don't have a textAlign model set
        switch (this.textPosition) {
            case PositionType.LEFT:
            case PositionType.BOTTOM_LEFT:
            case PositionType.TOP_LEFT:
                return HorizontalAlignType.LEFT;
            case PositionType.RIGHT:
            case PositionType.BOTTOM_RIGHT:
            case PositionType.TOP_RIGHT:
                return HorizontalAlignType.RIGHT;
            default:
                return HorizontalAlignType.CENTER;
        }
    }

    _build() {
        this.contentBlockFrame = this.addElement("contentBlockFrame", () => ContentBlockFrame);
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        let innerPadding = this.contentBlockFrame.hasBackdrop ? styles.innerPaddingWithBackdrop : styles.innerPaddingWithNoBackdrop;
        if (this.hasUserTransform) {
            // we don't need padding in this case
            innerPadding = 0;
        }

        let fillWidth = false;

        switch (this.textPosition) {
            case PositionType.BOTTOM:
            case PositionType.TOP:
                if (this.contentBlockFrame.hasBackdrop) {
                    innerPadding = 0;
                }
                fillWidth = true;
            case PositionType.FILL:
                fillWidth = true;
                break;
        }

        let innerBounds = new geom.Rect(0, 0, size).deflate(innerPadding);
        let availableContentSize = innerBounds.size;

        if (this.userWidth) {
            availableContentSize.width = Math.min(availableContentSize.width, this.userWidth);
        } else {
            switch (this.textPosition) {
                case PositionType.CENTER:
                    if (size.width > 300) {
                        // availableContentSize.width = availableContentSize.width - (getValueOrDefault(options.centerPaddingOffset, 20)); // this is to match pre Aug 2020 refactor
                        // availableContentSize.height = availableContentSize.height - (getValueOrDefault(options.centerPaddingOffset, 20)); // this is to match pre Aug 2020 refactor
                    }
                    break;
                case PositionType.TOP:
                case PositionType.BOTTOM:
                    // void
                    break;
                case PositionType.TOP_LEFT:
                case PositionType.LEFT:
                case PositionType.BOTTOM_LEFT:
                case PositionType.TOP_RIGHT:
                case PositionType.RIGHT:
                case PositionType.BOTTOM_RIGHT:
                    // availableContentSize.width = (availableContentSize.width / 2 - innerPadding * 2) - 20;  // this is to match pre Aug 2020 refactor
                    availableContentSize.width = availableContentSize.width / 2;
                    break;
                case PositionType.FILL:
                    break;
            }
        }

        let contentBlockFrameProps = children.setProps(this.contentBlockFrame, availableContentSize, {
            textAlign: this.model.textAlign || this.getDefaultTextAlign(),
            forceTextScale: options.forceTextScale,
            textPosition: this.textPosition,
            useContentWidth: !fillWidth && !this.userWidth
        });

        // special case to allow full width when text is left/right and doesn't fit
        if (!fillWidth && !this.userWidth && contentBlockFrameProps.children.contentBlocks.doesContentFit == false) {
            contentBlockFrameProps = children.setProps(this.contentBlockFrame, size.deflate(innerPadding), {
                textAlign: this.model.textAlign || this.getDefaultTextAlign(),
                forceTextScale: contentBlockFrameProps.children.contentBlocks.scale,
                textPosition: this.textPosition,
                useContentWidth: false
            });
        }

        let contentSize = contentBlockFrameProps.size;
        if (fillWidth) {
            // when fillWidth is set, force the contentSize to be the full width
            contentSize.width = innerBounds.width;
        }

        if (this.hasUserTransform) {
            // user positioned, limiting to the available bounds
            const userPositionX = Math.clamp(this.userPositionX * size.width, innerBounds.left, innerBounds.width - contentSize.width) / size.width;
            const userPositionY = Math.clamp(this.userPositionY * size.height, innerBounds.top, innerBounds.height - contentSize.height) / size.height;
            if (this.userPositionX !== userPositionX || this.userPositionY !== userPositionY) {
                this.model.userPositionX = userPositionX;
                this.model.userPositionY = userPositionY;
            }
            contentBlockFrameProps.bounds = new geom.Rect(userPositionX * size.width, userPositionY * size.height, contentSize);
        } else {
            // auto positioned using textPosition
            contentBlockFrameProps.bounds = new geom.Rect(0, 0, contentSize).positionInContainer(innerBounds, this.textPosition);
        }

        return { size, scale: contentBlockFrameProps.scale };
    }
}

class ContentBlockFrame extends BaseElement {
    get _canSelect() {
        return this.parentElement.canSelectContentBlockFrame;
    }

    get passThroughSelection() {
        return true;
    }

    get selectionPadding() {
        return this.hasBackdrop ? 0 : 20;
    }

    get canStyleText() {
        return this.parentElement.isOverImage;
    }

    get hasBackdrop() {
        return this.canStyleText && this.textStyle.contains("box");
    }

    get textStyle() {
        if (this.canStyleText) {
            return this.model.textStyle || "none";
        } else {
            return "none";
        }
    }

    _build() {
        this.contentBlocks = this.addElement("contentBlocks", () => ContentBlockCollection);
    }

    _loadStyles(styles) {
        if (this.hasBackdrop) {
            styles.paddingLeft = styles.paddingRight = styles.paddingTop = styles.paddingBottom = styles.backdropPadding;
        }
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        if (this.hasBackdrop) {
            let decorationProps = {
                type: "frame",
                shape: "rect"
            };

            switch (this.textStyle) {
                case "white_box":
                    decorationProps.fillColor = "white";
                    break;
                case "transparent_light_box":
                    decorationProps.fillColor = "white";
                    decorationProps.fillOpacity = 0.5;
                    break;
                case "transparent_dark_box":
                    decorationProps.fillColor = "black";
                    decorationProps.fillOpacity = 0.3;
                    break;
            }

            this.createDecoration(decorationProps);
        }

        if (options.useContentWidth) {
            let contentBlockProps = children.setProps(this.contentBlocks, size, {
                ...options,
                textStyle: this.textStyle,
                autoWidth: true
            });
            // use the width of the calculated content blocks as our width
            contentBlockProps.bounds = new geom.Rect(0, 0, contentBlockProps.size);
            return { size: contentBlockProps.size, scale: contentBlockProps.scale };
        } else {
            let contentBlockProps = children.setProps(this.contentBlocks, size, {
                ...options,
                textStyle: this.textStyle,
                autoWidth: false
            });

            // use the provided size as our width
            let contentX;
            switch (options.textAlign) {
                case HorizontalAlignType.LEFT:
                    contentX = 0;
                    break;
                case HorizontalAlignType.CENTER:
                    contentX = size.width / 2 - contentBlockProps.size.width / 2;
                    break;
                case HorizontalAlignType.RIGHT:
                    contentX = size.width - contentBlockProps.size.width;
                    break;
            }

            contentBlockProps.bounds = new geom.Rect(contentX, 0, contentBlockProps.size);
            return { size: new geom.Size(size.width, contentBlockProps.size.height), scale: contentBlockProps.scale };
        }
    }

    getBackgroundColor(forElement) {
        if (this.hasBackdrop && this.backdrop) {
            return app.currentTheme.palette.getColor(this.backdrop.styles.fillColor);
        } else {
            return super.getBackgroundColor(forElement);
        }
    }
}

export class ContentBlockCollection extends CollectionElement {
    get container() {
        return this.parentElement.parentElement;
    }

    get _canSelect() {
        return false;
        // return this.parentElement.canSelectContentBlockFrame;
    }

    // get passThroughSelection() {
    //     return true;
    // }

    // get selectionPadding() {
    //     return this.hasBackdrop ? 0 : 20;
    // }

    get minItemCount() {
        return 1;
    }

    get collectionPropertyName() {
        return "blocks";
    }

    getChildItemType(model) {
        return ContentBlockItem;
    }

    get defaultItemData() {
        return { type: this.options.defaultBlockType || ContentBlockType.TITLE };
        // return { type: this.container.defaultBlockType };
    }

    get availableBlockTypes() {
        if (this.options.allowedBlockTypes) {
            return this.options.allowedBlockTypes;
        }

        return [ContentBlockType.HEADLINE, ContentBlockType.HEADING, ContentBlockType.TITLE, ContentBlockType.BODY, ContentBlockType.CAPTION, ContentBlockType.BIGTEXT, ContentBlockType.LIST, ContentBlockType.DIVIDER, ContentBlockType.ICON];
    }

    get canAlign() {
        return this.options.canAlign || false;
    }

    get allowAlignment() {
        return true;
    }

    get firstTextLineHeight() {
        return this.itemElements[0].content.firstTextLineHeight;
    }

    get isTextFit() {
        return this.itemElements.every(element => element.content == null || element.content.isTextFit);
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        let textAlign = this.model.textAlign || options.textAlign || HorizontalAlignType.CENTER;

        let minScale = 0.5;
        let scaleDelta = 0.1;

        if (options.scaleToFit != undefined && options.scaleToFit == false) {
            minScale = 1;
        }

        let scale = options.forceTextScale || 1;

        let layouter = this.getLayouter(this.itemElements, size);
        let doesContentFit;

        const tryLayout = () => {
            doesContentFit = true;

            layouter.distributeVertically({
                horizontalAlign: textAlign,
                reserveMinHeights: true,
                gap: styles.verticalGap || 10,
                itemOptions: {
                    textAlign: textAlign,
                    autoWidth: options.autoWidth,
                    textColor: this.model.textColor,
                    forceTextScale: scale
                }
            });

            for (let item of this.itemElements) {
                if (item.content instanceof ContentBlockTextElement && item.content.isTextFit == false) {
                    doesContentFit = false;
                    break;
                }
            }
            if (layouter.size.height > size.height) {
                doesContentFit = false;
            }

            if ((!props.isFit || !doesContentFit) && scale - scaleDelta >= minScale) {
                scale -= scaleDelta;
                tryLayout();
            }
        };

        tryLayout();

        let contentBounds = null;
        for (let item of this.itemElements) {
            contentBounds = contentBounds ? contentBounds.union(item.bounds) : item.bounds;
        }

        if (!doesContentFit) {
            console.log("content blocks does not fit", this.toString());
        }

        // set custom text color (note: this has to happen AFTER the call to scaleStyleValues above because it is directly poking element styles)
        let fontColor;
        let fontFilter;

        switch (options.textStyle) {
            case "dark_text":
                fontColor = "primary_dark";
                break;
            case "white_text_with_shadow":
                fontColor = "white";
                fontFilter = "textShadow";
                break;
            case "transparent_dark_box":
            case "white_text":
                fontColor = "white";
                break;
        }

        for (let item of this.itemElements) {
            if (fontColor) {
                item.content.styles.fontColor = fontColor;
            }
            if (fontFilter) {
                item.content.styles.filter = fontFilter;
            }
        }

        return { size: contentBounds.size, scale, doesContentFit };
    }
}

export class ContentBlockItem extends CollectionItemElement {
    get minHeight() {
        return this.content.minHeight;
    }

    get selectionPadding() {
        return 0;
    }

    get rolloverPadding() {
        return 0;
    }

    get showRollover() {
        return false;
    }

    get _doubleClickToSelect() {
        return false;
    }

    get passThroughSelection() {
        return false;
    }

    get contentType() {
        return this.model.type || "text";
    }

    get textAlign() {
        return this.parentElement.textAlign;
    }

    _build() {
        switch (this.contentType) {
            case ContentBlockType.BIGTEXT:
                this.content = this.addElement("content", () => ContentBlockTextBigText, {
                    breakType: "even"
                });
                break;
            case ContentBlockType.HEADLINE:
                this.content = this.addElement("content", () => ContentBlockTextHeadline, {
                    breakType: "even"
                });
                break;
            case ContentBlockType.HEADING:
                this.content = this.addElement("content", () => ContentBlockTextHeading);
                break;
            case ContentBlockType.TITLE:
            case "subheading": // legacy
            case "label": // legacy
                this.content = this.addElement("content", () => ContentBlockTextTitle);
                break;
            case ContentBlockType.CAPTION:
                this.content = this.addElement("content", () => ContentBlockTextCaption);
                break;
            case ContentBlockType.DIVIDER:
                this.content = this.addElement("content", () => ContentBlockDivider);
                break;
            case ContentBlockType.LIST:
                this.content = this.addElement("content", () => ContentBlockList);
                break;
            case ContentBlockType.ICON:
                this.content = this.addElement("content", () => ContentBlockIcon, {
                    defaultAssetType: AssetType.ICON
                });
                break;
            case ContentBlockType.BULLET_POINT:
                this.content = this.addElement("content", () => ContentBlockBulletPoint);
                break;
            case "description": //legacy
            case ContentBlockType.BODY:
            default:
                this.content = this.addElement("content", () => ContentBlockTextBody);
                break;
        }

        // this.content.options.allowAlignment = this.parentElement.options.allowAlignment;
        this.content.options.allowAlignment = this.parentElement.allowAlignment;

        this.options.rollover = "ContentBlockItemContentRollover";
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        let textAlign = options.textAlign || this.textAlign;

        this.content.styles.textAlign = textAlign;

        let contentProps = children.setProps(this.content, size, options);
        contentProps.bounds = new geom.Rect(0, 0, contentProps.size);

        props.isTextFit = contentProps.isTextFit;

        // if (this.content instanceof TextElement && this.content.isTextFit == false) {
        // this.isTextFit = this.content.isTextFit;
        // size = this.content.calcSize(this.content.textBounds, options);
        // }
        // this.isTextFit = true;

        return { size: contentProps.size };
    }
}

export class ContentBlockTextElement extends TextElement {
    get selectionPadding() {
        if (this.calculatedProps.fontSize < 30) {
            return 5;
        } else {
            return 10;
        }
    }

    get uiOffset() {
        return 30;
    }

    get _doubleClickToSelect() {
        return false;
    }

    get allowedTextStyles() {
        return _.pull([...this.findClosestOfType(ContentBlockCollection).availableBlockTypes], ContentBlockType.DIVIDER, ContentBlockType.ICON);
    }

    get textStylePropertyName() {
        return "type";
    }

    get allowStyling() {
        return true;
    }

    get allowUserScale() {
        return true;
    }

    get singleLine() {
        return true;
    }

    get allowAlignment() {
        return this.options.allowAlignment != undefined ? this.options.allowAlignment : true;
    }

    get autoHeight() {
        return true;
    }

    get constrainWidth() {
        return true;
    }

    get scaleTextToFit() {
        return false;
    }

    get allowBlockType() {
        return true;
    }

    getSlideColor(options = {}) {
        if (this.findClosestOfType(NodeElement)) {
            return this.findClosestOfType(NodeElement).getSlideColor(options);
        } else {
            return super.getSlideColor(options);
        }
    }
}

class ContentBlockTextBigText extends ContentBlockTextElement {
    // get textLayoutType() {
    //     return "even";
    // }

    get allowedBreakTypes() {
        return [TextBreakType.EVEN, TextBreakType.DEFAULT];
    }

    get placeholderPrompt() {
        return "Type text";
    }
}

class ContentBlockTextHeadline extends ContentBlockTextElement {
    get allowedBreakTypes() {
        return [TextBreakType.EVEN, TextBreakType.DEFAULT];
    }

    get placeholderPrompt() {
        return "Type headline";
    }
}

class ContentBlockTextTitle extends ContentBlockTextElement {
    get placeholderPrompt() {
        return "Type title";
    }
}

class ContentBlockTextBody extends ContentBlockTextElement {
    get placeholderPrompt() {
        return "Type text";
    }
}

class ContentBlockTextCaption extends ContentBlockTextElement {
    get placeholderPrompt() {
        return "Type caption";
    }
}

class ContentBlockTextHeading extends ContentBlockTextElement {
    get placeholderPrompt() {
        return "Type heading";
    }
}

class ContentBlockIcon extends AdjustableContentElement {
    get minHeight() {
        return this.styles.height;
    }

    get defaultOverlayType() {
        return "ContentElementDefaultOverlay";
    }
}

class ContentBlockBulletPoint extends BaseElement {
    _build() {
        this.bullet = this.addElement("bullet", () => SVGCircleElement);
        this.text = this.addElement("text", () => TextGroup, {
            title: {
                singleLine: true
            },
            body: {
                allowParagraphStyles: true,
                allowEmptyLines: false,
                placeholder: "Type or hit enter to add another bullet"
            }
        });
    }

    _calcProps(props, options) {
        let { size, styles, children } = props;

        let layouter = this.getLayouter([this.bullet, this.text], size);
        layouter.calcHorizontalBlockLayout({
            contentSize: new geom.Size(styles.bullet.width, styles.bullet.height),
            verticalAlign: layouter.VerticalBlockAlignType.MIDDLE_TITLE,
        });

        props.isFit = this.text.isTextFit;

        return { size: layouter.size };
    }
}

class ContentBlockList extends BulletPoints {
    get minHeight() {
        return 100;
        // return this.itemElements.reduce((acc, item) => acc + item.minHeight + this.styles.vGap, -this.styles.vGap);
    }

    get _canSelect() {
        return true;
    }

    get requireParentSelection() {
        return true;
    }

    get passThroughSelection() {
        return false;
    }

    get selectionPadding() {
        return 30;
    }
}

class ContentBlockDivider extends BaseElement {
    get minHeight() {
        return this.styles.marginTop + this.styles.marginBottom + this.styles.paddingTop + this.styles.paddingBottom;// + this.dividerHeight;
    }

    get showRollover() {
        return true;
    }

    _build() {
        this.divider = this.addElement("divider", () => SVGPolylineElement);
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;
        const PADDING = 50;
        let y = 5;

        if (options.autoWidth) {
            size.width = 100;
        }
        if (options.textColor) {
            this.divider.styles.strokeColor = options.textColor;
        }

        children.setProps(this.divider, {
            path: [[0, y], [size.width, y]]
        });

        return { size: new geom.Size(size.width, 11) };
    }
}

